import { Tabs, TabsList, TabsTrigger } from '@ibsys/kit';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { RunStatus, RunTaskDetail, useRun, useRuns } from '@/entities/jobs';
import { RunsTable } from './RunsTable';
import { TaskDetailDrawer } from './TaskDetailDrawer';
import { PageContainer } from '@/shared/ui';

const STATUS_TABS: (RunStatus | 'all')[] = ['all', 'PENDING', 'RUNNING', 'SUCCESS', 'FAILED', 'CANCELLED'];
const POLLING_INTERVAL = 3000;

// Проверка значения вкладки на принадлежность списку допустимых
const isValidTab = (value: string | null): value is RunStatus | 'all' =>
  value !== null && (STATUS_TABS as string[]).includes(value);

export const RunsPage: FC = () => {
  const { t } = useTranslation(['common', 'runs']);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: RunStatus | 'all' = isValidTab(tabParam) ? tabParam : 'all';
  const runIdParam = searchParams.get('runId');
  const taskIdParam = searchParams.get('taskId');

  const [refreshKey, setRefreshKey] = useState(0);
  // Сериализованное предыдущее значение агрегаций для сравнения
  const prevByStatusRef = useRef<string | null>(null);

  const status = activeTab === 'all' ? undefined : activeTab;

  // Минимальный запрос только для получения total и агрегаций
  const { data: runsData } = useRuns({ status, limit: 1 }, { refetchInterval: POLLING_INTERVAL });

  // Сбрасываем кеш DataGrid только если агрегации по статусам изменились
  useEffect(() => {
    const byStatus = runsData?.aggregations?.byStatus;
    if (!byStatus) return;
    const serialized = JSON.stringify(byStatus);
    if (prevByStatusRef.current !== null && prevByStatusRef.current !== serialized) {
      setRefreshKey((k) => k + 1);
    }
    prevByStatusRef.current = serialized;
  }, [runsData]);

  // Параметры drawer-а синхронизированы с URL для открытия по прямой ссылке
  const isDrawerOpen = !!(runIdParam && taskIdParam);
  const { data: drawerRun } = useRun(runIdParam ?? '', { enabled: isDrawerOpen, retry: 0 });
  const drawerTask: RunTaskDetail | null = useMemo(() => {
    if (!taskIdParam || !drawerRun) return null;
    return drawerRun.tasks.find((task) => String(task.id) === taskIdParam) ?? null;
  }, [taskIdParam, drawerRun]);

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        prev.set('tab', value);
        prev.delete('runId');
        prev.delete('taskId');
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleTaskClick = useCallback(
    (runId: string, task: RunTaskDetail) => {
      setSearchParams((prev) => {
        prev.set('runId', runId);
        prev.set('taskId', String(task.id));
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      setSearchParams((prev) => {
        prev.delete('runId');
        prev.delete('taskId');
        return prev;
      });
    },
    [setSearchParams],
  );

  return (
    <PageContainer enableScroll={false}>
      <div className="flex flex-col h-full gap-4 w-full">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab} value={tab}>
                {t(`runs:tabs.${tab}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex-1 overflow-hidden">
          <RunsTable
            total={runsData?.total ?? 0}
            status={status}
            refreshKey={refreshKey}
            onTaskClick={handleTaskClick}
          />
        </div>
      </div>

      <TaskDetailDrawer
        task={drawerTask}
        runId={runIdParam ?? ''}
        open={isDrawerOpen && drawerTask !== null}
        onOpenChange={handleDrawerOpenChange}
      />
    </PageContainer>
  );
};
